/**
 * Guest claim helpers.
 *
 * Reads the guest userId from the `mol_guest_uid` cookie (set by the server
 * when provisioning a guest session) and stashes it in sessionStorage so the
 * auth-default bond can transfer projects after login/signup.
 */

/** Read a cookie value by name. */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

/**
 * Whether a guest session exists in this browser (the `mol_guest_uid` cookie
 * is set exclusively for guest sessions, and cleared on claim).
 */
export function hasGuestCookie(): boolean {
  return typeof document !== 'undefined' && /(?:^|;\s*)mol_guest_uid=/.test(document.cookie)
}

/**
 * The app-wide guest test for a `useAuth()` user. Guests normally have NO
 * client auth state (`authConfig.shouldRestoreUser` rejects anonymous restores,
 * so `!user` holds) — but if an anonymous user row ever reaches client state
 * anyway (an older bundle hydrated it, a future flow sets it), `isAnonymous`
 * still marks it. Every guest check goes through here so no surface treats an
 * anonymous user as signed in.
 * @param user - The current `useAuth()` user (or anything user-shaped).
 * @returns `true` when there is no signed-in, non-anonymous user.
 */
export function isGuestUser(user: unknown): boolean {
  return !user || (user as { isAnonymous?: boolean }).isAnonymous === true
}

/**
 * If the current session is a guest (mol_guest_uid cookie exists), stash the
 * userId in sessionStorage for the post-auth claim flow.
 */
export function stashGuestUserId(): void {
  const guestUid = getCookie('mol_guest_uid')
  if (guestUid) {
    sessionStorage.setItem('mol_guest_user_id', guestUid)
  }
}
